import moment from 'moment';
import firebase from 'src/utils/firebase';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { socketClient } from '../index';

const normalizeShopPayload = (payload) => payload?.shop || payload || {};

const emitScopedSettingsRequest = ({
  emitEvent,
  successEvent,
  errorEvent,
  payload,
  timeoutMessage,
  mapSuccess
}) => socketClient.waitUntilConnected().then(() => new Promise((resolve, reject) => {
  let settled = false;

  const cleanup = () => {
    socketClient.off(successEvent, handleSuccess);
    socketClient.off(errorEvent, handleError);
  };

  const fail = (error) => {
    if (settled) return;
    settled = true;
    clearTimeout(timeoutId);
    cleanup();
    reject(error instanceof Error ? error : new Error(String(error || timeoutMessage)));
  };

  const handleSuccess = (response) => {
    if (response?.requestId !== payload.requestId) {
      return;
    }

    if (settled) return;
    settled = true;
    clearTimeout(timeoutId);
    cleanup();
    resolve(mapSuccess ? mapSuccess(response) : response);
  };

  const handleError = (response) => {
    if (response?.requestId !== payload.requestId) {
      return;
    }

    fail(new Error(response?.message || timeoutMessage));
  };

  const timeoutId = setTimeout(() => {
    fail(new Error(timeoutMessage));
  }, 10000);

  Promise.all([
    socketClient.on(successEvent, handleSuccess),
    socketClient.on(errorEvent, handleError)
  ])
    .then(() => socketClient.emit(emitEvent, payload))
    .catch(fail);
}));

const initialState = {
  loading: true,
  uploadLogoLoading: false,
  shopInfoSaving: false,
  error: null,
  shop: {},
  stripeCustomerId: '',
  ownerId: '',
  url: '',
  systemMessage: '',
  trialLeft: '',
};

export const requestGetShopData = createAsyncThunk('settings/requestGetShopData', async (_, { rejectWithValue }) => {
  try {
    const idToken = await firebase.auth().currentUser.getIdToken();
    const requestId = `settings:shop:${Date.now()}:${Math.random().toString(36).slice(2)}`;

    return await emitScopedSettingsRequest({
      emitEvent: 'getShopData',
      successEvent: 'getShopDataSuccess',
      errorEvent: 'getShopDataError',
      payload: { idToken, requestId },
      timeoutMessage: 'Timed out while loading shop settings',
      mapSuccess: normalizeShopPayload
    });
  } catch (error) {
    return rejectWithValue(error.message || 'Unable to load shop settings');
  }
});

export const uploadLogo = createAsyncThunk('settings/uploadLogo', async (payload, { rejectWithValue }) => {
  try {
    const idToken = await firebase.auth().currentUser.getIdToken();
    const requestId = `settings:logo:${Date.now()}:${Math.random().toString(36).slice(2)}`;
    const file = payload.file;
    const storageRef = firebase.storage().ref();
    const fileRef = storageRef.child(`${file.name} ${moment().format('YYYYMMDDHHmmss')}`);
    await fileRef.put(file);
    const currentUrl = await fileRef.getDownloadURL();

    return await emitScopedSettingsRequest({
      emitEvent: 'uploadLogo',
      successEvent: 'uploadLogoSuccess',
      errorEvent: 'uploadLogoError',
      payload: { idToken, url: currentUrl, target: payload.target || 'service', requestId },
      timeoutMessage: `Timed out while uploading ${payload.target === 'dealership' ? 'dealership' : 'shop'} logo`,
      mapSuccess: normalizeShopPayload
    });
  } catch (error) {
    return rejectWithValue(error.message || 'Unable to upload shop logo');
  }
});

export const shopInfoUpdate = createAsyncThunk('settings/shopInfoUpdate', async (payload, { rejectWithValue }) => {
  try {
    const idToken = await firebase.auth().currentUser.getIdToken();
    const requestId = `settings:update:${Date.now()}:${Math.random().toString(36).slice(2)}`;

    return await emitScopedSettingsRequest({
      emitEvent: 'shopInfoUpdate',
      successEvent: 'shopInfoUpdateSuccess',
      errorEvent: 'shopInfoUpdateError',
      payload: { idToken, requestId, ...payload },
      timeoutMessage: 'Timed out while saving shop settings',
      mapSuccess: normalizeShopPayload
    });
  } catch (error) {
    return rejectWithValue(error.message || 'Unable to save shop settings');
  }
});

const applyShopData = (state, shop) => {
  state.shop = shop || {};
  state.stripeCustomerId = shop?.stripe_customer_id || null;
  state.ownerId = shop?.owner_id || '';
  state.url = shop?.shop_logo || '';
};

const slice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setSystemMessage(state, action) {
      state.systemMessage = action.payload;
    },
    clearSettingsError(state) {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestGetShopData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestGetShopData.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        applyShopData(state, action.payload);
      })
      .addCase(requestGetShopData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || 'Unable to load shop settings';
      })
      .addCase(uploadLogo.pending, (state) => {
        state.uploadLogoLoading = true;
        state.error = null;
      })
      .addCase(uploadLogo.fulfilled, (state, action) => {
        state.uploadLogoLoading = false;
        state.error = null;
        applyShopData(state, action.payload);
      })
      .addCase(uploadLogo.rejected, (state, action) => {
        state.uploadLogoLoading = false;
        state.error = action.payload || action.error?.message || 'Unable to upload shop logo';
      })
      .addCase(shopInfoUpdate.pending, (state) => {
        state.shopInfoSaving = true;
        state.error = null;
      })
      .addCase(shopInfoUpdate.fulfilled, (state, action) => {
        state.shopInfoSaving = false;
        state.error = null;
        applyShopData(state, action.payload);
      })
      .addCase(shopInfoUpdate.rejected, (state, action) => {
        state.shopInfoSaving = false;
        state.error = action.payload || action.error?.message || 'Unable to save shop settings';
      });
  }
});

export const { setSystemMessage, clearSettingsError } = slice.actions;

export const reducer = slice.reducer;
